import { Fragment, useMemo, useState } from 'react'
import { downloadFile } from '../api/client'
import { AbsenceDialog } from '../components/AbsenceDialog'
import { EntryDialog } from '../components/EntryDialog'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card, CardBody, CardHeader } from '../components/ui/Card'
import { Dialog } from '../components/ui/Dialog'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { EmptyState } from '../components/ui/EmptyState'
import { Field, Input, Select } from '../components/ui/Field'
import { DateInput, TimeInput } from '../components/ui/DateTimeInputs'
import { PageLoader } from '../components/ui/Spinner'
import { useT } from '../i18n/context'
import {
    formatDurationCompact,
    formatDateKeyShort,
    formatDateTime,
    formatHours,
    formatHoursSigned,
    formatMonthYear,
    formatTime,
    formatWeekdayShort,
    fromLocalInputValue,
    toLocalInputValue,
} from '../lib/format'
import { errText } from '../lib/errors'
import { ABSENCE_TYPES, entryTypeMeta } from '../lib/labelMaps'
import { useAuthStore } from '../stores/auth'
import { toast } from '../stores/toast'
import { useActiveWorkspaceId, useCanManage } from '../stores/workspace'
import {
    useCloseMonthMutations,
    useHolidayMarkMutations,
    useHolidayMarks,
    useWorkingTimeMonthly,
    workingTimeMonthlyQueryString,
} from '../hooks/useWorkingTimeApi'
import {
    useClients,
    useEntryMutations,
    useProjects,
    useTimeEntriesAll,
} from '../hooks/useApi'
import { useMemberMutations, useMembers } from '../hooks/useWorkspaceApi'
import type {
    TimeEntry,
    WorkingTimeDayRow,
    WorkingTimeMemberInfo,
} from '../types/api'

const pad2 = (n: number) => String(n).padStart(2, '0')

/** „Mo 07.09." aus dem lokalen YYYY-MM-DD-Key der Tageszeile. */
function dayLabel(dateKey: string): string {
    return `${formatWeekdayShort(dateKey)} ${formatDateKeyShort(dateKey)}`
}

function rangeLabel(from: string, to: string): string {
    return from === to
        ? formatDateKeyShort(from)
        : `${formatDateKeyShort(from)} – ${formatDateKeyShort(to)}`
}

/** Abwesenheits-Spalte: Summe + Kürzel des (einzigen) Typs. */
function absenceCell(day: WorkingTimeDayRow, t: ReturnType<typeof useT>) {
    const total = day.sickSeconds + day.vacationSeconds + day.timeOffSeconds
    if (total === 0) return <span className='text-slate-500 dark:text-slate-400'>—</span>
    const kinds: string[] = []
    if (day.sickSeconds > 0) kinds.push(t('workingTime.absenceSickShort'))
    if (day.vacationSeconds > 0)
        kinds.push(t('workingTime.absenceVacationShort'))
    if (day.timeOffSeconds > 0)
        kinds.push(t('workingTime.absenceTimeOffShort'))
    return (
        <span>
            {formatHours(total)}
            <span className='ml-1 text-xs text-slate-500 dark:text-slate-400'>
                ({kinds.join(' ')})
            </span>
        </span>
    )
}

/**
 * Setup-Gate (Konzept F1): Ohne Starttermin, Kernarbeitszeit-Fenster,
 * Wochenarbeitszeit und Urlaubstage/Jahr gibt es ein Formular statt
 * der Tabelle. Eintragbar: Mitglied selbst oder OWNER/ADMIN (Backend).
 */
function SetupCard({
    member,
    workspaceId,
}: {
    member: WorkingTimeMemberInfo
    workspaceId: number
}) {
    const t = useT()
    const update = useMemberMutations(workspaceId).update

    const [initializedFor, setInitializedFor] = useState<number | null>(null)
    const [start, setStart] = useState('')
    const [coreFrom, setCoreFrom] = useState('')
    const [coreTo, setCoreTo] = useState('')
    const [weeklyHours, setWeeklyHours] = useState('')
    const [vacationDays, setVacationDays] = useState('')

    if (initializedFor !== member.userId) {
        setInitializedFor(member.userId)
        setStart(member.workingTimeStart ? member.workingTimeStart.slice(0, 10) : '')
        setCoreFrom(member.coreTimeFrom ?? '')
        setCoreTo(member.coreTimeTo ?? '')
        setWeeklyHours(member.weeklyTargetHours !== null ? String(member.weeklyTargetHours) : '')
        setVacationDays(
            member.vacationDaysPerYear !== null ? String(member.vacationDaysPerYear) : '',
        )
    }

    const save = () => {
        if (!start || !coreFrom || !coreTo || !weeklyHours || !vacationDays) {
            toast.error(t('workingTime.setupIncomplete'))
            return
        }
        if (coreFrom >= coreTo) {
            toast.error(t('workingTime.setupCoreTimeInvalid'))
            return
        }
        update.mutate(
            {
                userId: member.userId,
                weeklyTargetHours: Number(weeklyHours),
                coreTimeFrom: coreFrom,
                coreTimeTo: coreTo,
                vacationDaysPerYear: Number(vacationDays),
                workingTimeStart: fromLocalInputValue(`${start}T00:00`),
            },
            {
                onSuccess: () => toast.success(t('workingTime.setupSaved')),
                onError: err => toast.error(errText(err)),
            },
        )
    }

    return (
        <Card>
            <CardHeader
                title={t('workingTime.setupTitle')}
                subtitle={t('workingTime.setupDescription')}
            />
            <CardBody>
                <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
                    <Field label={t('workingTime.setupStart')}>
                        <DateInput value={start} onChange={setStart} />
                    </Field>
                    <Field label={t('workingTime.setupCoreFrom')}>
                        <TimeInput value={coreFrom} onChange={setCoreFrom} />
                    </Field>
                    <Field label={t('workingTime.setupCoreTo')}>
                        <TimeInput value={coreTo} onChange={setCoreTo} />
                    </Field>
                    <Field label={t('workingTime.setupWeeklyHours')}>
                        <Input
                            type='number'
                            min='0.01'
                            step='0.25'
                            value={weeklyHours}
                            onChange={e => setWeeklyHours(e.target.value)}
                        />
                    </Field>
                    <Field label={t('workingTime.setupVacationDays')}>
                        <Input
                            type='number'
                            min='0'
                            step='0.5'
                            value={vacationDays}
                            onChange={e => setVacationDays(e.target.value)}
                        />
                    </Field>
                </div>
                <div className='mt-4'>
                    <Button
                        onClick={save}
                        loading={update.isPending}
                    >
                        {t('common.save')}
                    </Button>
                </div>
            </CardBody>
        </Card>
    )
}

export function MonthlyHoursPage() {
    const t = useT()
    const workspaceId = useActiveWorkspaceId()
    const canManage = useCanManage()
    const me = useAuthStore(s => s.user)
    const members = useMembers(workspaceId)

    const now = new Date()
    const [year, setYear] = useState(now.getFullYear())
    const [month, setMonth] = useState(now.getMonth() + 1)
    const [memberUserId, setMemberUserId] = useState<number | null>(null)

    const effectiveUserId = memberUserId ?? (canManage ? (me?.id ?? null) : null)
    const query = useWorkingTimeMonthly(workspaceId, effectiveUserId, year, month)
    const data = query.data
    /** Nacht-Spalte nur anzeigen, wenn Nacht-Anteile existieren (Phase 3) */
    const showNight = (data?.monthTotals.nightSeconds ?? 0) > 0

    // Einträge des Monats für die von/bis-Detailzeilen: Die Monthly-Query
    // liefert nur Tagesaggregate, die Zeiten stecken in den Einträgen.
    // userId analog zur Monthly-Query (Verwalter explizit, Mitglied entfällt
    // = eigenes Konto).
    const monthEndDay = new Date(year, month, 0).getDate()
    const monthEntries = useTimeEntriesAll(workspaceId, {
        ...(effectiveUserId !== null ? { userId: effectiveUserId } : {}),
        startDate: fromLocalInputValue(`${year}-${pad2(month)}-01T00:00`),
        endDate: fromLocalInputValue(
            `${year}-${pad2(month)}-${pad2(monthEndDay)}T23:59`,
        ),
    })
    /** Einträge je lokalem Tages-Key (YYYY-MM-DD), chronologisch. */
    const entriesByDay = useMemo(() => {
        const map = new Map<string, TimeEntry[]>()
        for (const e of monthEntries.data ?? []) {
            const key = toLocalInputValue(e.startTime).slice(0, 10)
            const list = map.get(key)
            if (list) list.push(e)
            else map.set(key, [e])
        }
        for (const list of map.values()) {
            list.sort((a, b) => a.startTime.localeCompare(b.startTime))
        }
        return map
    }, [monthEntries.data])

    // Zeilen-Aktionen (F2): Feiertag markieren, Abwesenheit eintragen.
    // OWNER/ADMIN agieren hier für das gewählte Mitglied (userId im Body).
    const marks = useHolidayMarks(workspaceId, effectiveUserId, year, month)
    const { upsert: upsertMark, remove: removeMark } =
        useHolidayMarkMutations(workspaceId)
    const { close: closeMonth, reopen: reopenMonth } =
        useCloseMonthMutations(workspaceId)
    const markUserId =
        effectiveUserId !== null && effectiveUserId !== me?.id
            ? effectiveUserId
            : undefined
    const [holidayDialog, setHolidayDialog] = useState<{
        date: string
        factor: string
        note: string
    } | null>(null)
    const [absenceDate, setAbsenceDate] = useState<string | null>(null)
    const [workDate, setWorkDate] = useState<string | null>(null)
    const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null)
    const [deleteEntry, setDeleteEntry] = useState<TimeEntry | null>(null)
    const [expandedDays, setExpandedDays] = useState<ReadonlySet<string>>(
        new Set(),
    )
    // Anderer Monat/Mitglied → andere Tage: Expansion beim Wechsel
    // zurücksetzen (Render-Zeit-Reset wie initializedFor in den Dialogen).
    const expandedCtx = `${year}-${month}-${effectiveUserId ?? 'self'}`
    const [expandedFor, setExpandedFor] = useState(expandedCtx)
    if (expandedFor !== expandedCtx) {
        setExpandedFor(expandedCtx)
        setExpandedDays(new Set())
    }
    const [confirmClose, setConfirmClose] = useState(false)
    const [confirmReopen, setConfirmReopen] = useState(false)
    const { remove: removeEntry } = useEntryMutations(workspaceId)

    const rowActionsDisabled = data?.monthClosed === true

    const toggleDay = (date: string) => {
        setExpandedDays(prev => {
            const next = new Set(prev)
            if (next.has(date)) next.delete(date)
            else next.add(date)
            return next
        })
    }

    const toggleHoliday = (day: WorkingTimeDayRow) => {
        if (day.isHoliday) {
            const mark = marks.data?.find(m => m.date === day.date)
            setHolidayDialog({
                date: day.date,
                factor: mark?.factor != null ? String(mark.factor) : '',
                note: mark?.note ?? '',
            })
            return
        }
        upsertMark.mutate(
            {
                date: day.date,
                ...(markUserId !== undefined ? { userId: markUserId } : {}),
            },
            {
                onSuccess: () => toast.success(t('workingTime.holidayMarked')),
                onError: err => toast.error(errText(err)),
            },
        )
    }

    const prevMonth = () => {
        if (month === 1) {
            setMonth(12)
            setYear(year - 1)
        } else {
            setMonth(month - 1)
        }
    }
    const nextMonth = () => {
        if (month === 12) {
            setMonth(1)
            setYear(year + 1)
        } else {
            setMonth(month + 1)
        }
    }

    const monthName = formatMonthYear(year, month)

    if (workspaceId === null) return null

    const exportPdf = () => {
        downloadFile(
            `/working-time/monthly-pdf?${workingTimeMonthlyQueryString(
                workspaceId,
                effectiveUserId,
                year,
                month,
            )}`,
            `${t('workingTime.pdfName')}-${year}-${String(month).padStart(2, '0')}.pdf`,
        ).catch(() => toast.error(t('common.exportFailed')))
    }

    const handleCloseMonth = () => {
        closeMonth.mutate(
            // userId entfällt = eigenes Konto (Mitglied darf selbst
            // abschließen, Konzept F9 — M1-Fix)
            { userId: effectiveUserId ?? undefined, year, month },
            {
                onSuccess: () => {
                    toast.success(t('workingTime.closedToast'))
                    setConfirmClose(false)
                },
                onError: err => toast.error(errText(err)),
            },
        )
    }

    const handleReopenMonth = () => {
        reopenMonth.mutate(
            { userId: effectiveUserId ?? undefined, year, month },
            {
                onSuccess: () => {
                    toast.success(t('workingTime.reopenedToast'))
                    setConfirmReopen(false)
                },
                onError: err => toast.error(errText(err)),
            },
        )
    }

    const exportCsv = () => {
        downloadFile(
            `/working-time/export?${workingTimeMonthlyQueryString(
                workspaceId,
                effectiveUserId,
                year,
                month,
            )}`,
            `${t('workingTime.csvName')}-${year}-${String(month).padStart(2, '0')}.csv`,
        ).catch(() => toast.error(t('common.exportFailed')))
    }

    return (
        <div className='space-y-4'>
            {/* Kopf: Monat-Navigation + Mitglieder-Filter + Export */}
            <Card>
                <CardBody className='flex flex-wrap items-end gap-2'>
                    <div className='flex items-end gap-1'>
                        <Button
                            variant='secondary'
                            size='sm'
                            onClick={prevMonth}
                            aria-label={t('workingTime.prevMonth')}
                        >
                            ‹
                        </Button>
                        <div className='min-w-44 px-2 py-1.5 text-center text-sm font-semibold tabular-nums'>
                            {monthName}
                        </div>
                        <Button
                            variant='secondary'
                            size='sm'
                            onClick={nextMonth}
                            aria-label={t('workingTime.nextMonth')}
                        >
                            ›
                        </Button>
                    </div>
                    {canManage && (
                        <Field label={t('workingTime.member')}>
                            <Select
                                value={String(effectiveUserId ?? '')}
                                onChange={e =>
                                    setMemberUserId(Number(e.target.value))
                                }
                            >
                                {(members.data ?? []).map(m => (
                                    <option key={m.userId} value={m.userId}>
                                        {m.user.name}
                                    </option>
                                ))}
                            </Select>
                        </Field>
                    )}
                    <div className='ml-auto flex flex-wrap items-center justify-end gap-2'>
                        {data && (
                            <Badge tone={data.monthClosed ? 'gray' : 'amber'}>
                                {data.monthClosed
                                    ? t('workingTime.statusClosed')
                                    : t('workingTime.statusOpen')}
                            </Badge>
                        )}
                        {data?.setupComplete &&
                            (data.monthClosed ? (
                                <Button
                                    variant='secondary'
                                    size='sm'
                                    onClick={() => setConfirmReopen(true)}
                                    loading={reopenMonth.isPending}
                                >
                                    {t('workingTime.reopenMonth')}
                                </Button>
                            ) : (
                                <Button
                                    variant='secondary'
                                    size='sm'
                                    onClick={() => setConfirmClose(true)}
                                    loading={closeMonth.isPending}
                                >
                                    {t('workingTime.closeMonth')}
                                </Button>
                            ))}
                        <Button
                            variant='secondary'
                            size='sm'
                            onClick={() => {
                                void query.refetch()
                                toast.success(t('workingTime.importedMonth'))
                            }}
                            disabled={!data?.setupComplete || data?.monthClosed}
                        >
                            {t('workingTime.importMonth')}
                        </Button>
                        <Button
                            variant='secondary'
                            size='sm'
                            onClick={exportPdf}
                            disabled={!data?.setupComplete}
                        >
                            {t('workingTime.pdfExport')}
                        </Button>
                        <Button
                            variant='secondary'
                            size='sm'
                            onClick={exportCsv}
                            disabled={!data?.setupComplete}
                        >
                            {t('workingTime.csvExport')}
                        </Button>
                    </div>
                </CardBody>
            </Card>

            {data?.monthClosed && (
                <div className='flex items-center gap-2 rounded-lg bg-slate-100 px-4 py-2.5 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300'>
                    <Badge tone='gray'>{t('workingTime.statusClosed')}</Badge>
                    <span>
                        {t('workingTime.closedInfo', {
                            date: data.closedAt
                                ? formatDateTime(data.closedAt)
                                : '',
                        })}
                    </span>
                </div>
            )}

            {query.isLoading && <PageLoader />}

            {query.isError && (
                <Card>
                    <CardBody>
                        <EmptyState
                            title={t('workingTime.loadErrorTitle')}
                            description={errText(query.error)}
                        />
                    </CardBody>
                </Card>
            )}

            {data && !data.setupComplete && (
                <SetupCard member={data.member} workspaceId={workspaceId} />
            )}

            {data && data.setupComplete && (
                <>
                    {/* Kopf-KPIs: Rest-Urlaub, Überstundenguthaben, Monatssaldo */}
                    <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                        <Card>
                            <CardBody>
                                <div className='text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400'>
                                    {t('workingTime.kpiVacation')}
                                </div>
                                <div className='mt-1 text-2xl font-bold tabular-nums'>
                                    {data.vacation.remainingDays.toLocaleString(
                                        undefined,
                                        { maximumFractionDigits: 1 },
                                    )}
                                </div>
                                <div className='mt-1 text-xs text-slate-500 dark:text-slate-400'>
                                    {t('workingTime.kpiVacationHint', {
                                        entitlement:
                                            data.vacation.entitlementDays.toLocaleString(
                                                undefined,
                                                { maximumFractionDigits: 1 },
                                            ),
                                        used: data.vacation.usedDays.toLocaleString(
                                            undefined,
                                            { maximumFractionDigits: 1 },
                                        ),
                                    })}
                                </div>
                            </CardBody>
                        </Card>
                        <Card>
                            <CardBody>
                                <div className='text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400'>
                                    {t('workingTime.kpiOvertime')}
                                </div>
                                <div className='mt-1 text-2xl font-bold tabular-nums'>
                                    {data.overtime.balanceHours.toLocaleString(
                                        undefined,
                                        { maximumFractionDigits: 2 },
                                    )}{' '}
                                    h
                                </div>
                                <div className='mt-1 text-xs text-slate-500 dark:text-slate-400'>
                                    {t('workingTime.kpiOvertimeHint', {
                                        credit:
                                            data.overtime.creditHours.toLocaleString(
                                                undefined,
                                                { maximumFractionDigits: 2 },
                                            ),
                                        used: data.overtime.usedHours.toLocaleString(
                                            undefined,
                                            { maximumFractionDigits: 2 },
                                        ),
                                    })}
                                </div>
                            </CardBody>
                        </Card>
                        <Card>
                            <CardBody>
                                <div className='text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400'>
                                    {t('workingTime.kpiMonthSaldo')}
                                </div>
                                <div
                                    className={`mt-1 text-2xl font-bold tabular-nums ${
                                        data.monthTotals.saldoSeconds > 0
                                            ? 'text-emerald-600 dark:text-emerald-400'
                                            : data.monthTotals.saldoSeconds < 0
                                              ? 'text-rose-600 dark:text-rose-400'
                                              : ''
                                    }`}
                                >
                                    {formatHoursSigned(
                                        data.monthTotals.saldoSeconds,
                                    )}
                                </div>
                                <div className='mt-1 text-xs text-slate-500 dark:text-slate-400'>
                                    {t('workingTime.kpiMonthSaldoHint', {
                                        target: formatHours(
                                            data.monthTotals.targetSeconds,
                                        ),
                                        worked: formatHours(
                                            data.monthTotals.workedSeconds,
                                        ),
                                    })}
                                </div>
                            </CardBody>
                        </Card>
                    </div>

                    {/* Wochenblöcke: eine Card je Kalenderwoche */}
                    {data.weeks.map(week => (
                        <Card key={`${week.isoWeek}-${week.from}`}>
                            <CardHeader
                                title={`${t('workingTime.weekLabel')} ${week.isoWeek}`}
                                subtitle={rangeLabel(week.from, week.to)}
                                actions={
                                    <Button
                                        variant='ghost'
                                        size='sm'
                                        disabled={rowActionsDisabled}
                                        onClick={() => {
                                            void query.refetch()
                                            toast.success(
                                                t('workingTime.importedWeek'),
                                            )
                                        }}
                                    >
                                        {t('workingTime.importWeek')}
                                    </Button>
                                }
                            />
                            <CardBody className='!px-0 !py-0'>
                                <div className='overflow-x-auto'>
                                    <table className='w-full text-sm'>
                                        <thead>
                                            <tr className='border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:text-slate-400'>
                                                <th className='px-4 py-2.5'>
                                                    {t('workingTime.colDate')}
                                                </th>
                                                <th className='px-4 py-2.5 text-right'>
                                                    {t('workingTime.colTarget')}
                                                </th>
                                                <th className='px-4 py-2.5 text-right'>
                                                    {t('workingTime.colWorked')}
                                                </th>
                                                {showNight && (
                                                    <th className='px-4 py-2.5 text-right'>
                                                        {t('workingTime.colNight')}
                                                    </th>
                                                )}
                                                <th className='px-4 py-2.5 text-right'>
                                                    {t('workingTime.colBreak')}
                                                </th>
                                                <th className='px-4 py-2.5 text-right'>
                                                    {t('workingTime.colAbsence')}
                                                </th>
                                                <th className='px-4 py-2.5 text-right'>
                                                    {t('workingTime.colSaldo')}
                                                </th>
                                                <th className='px-4 py-2.5 text-right'>
                                                    {t('workingTime.colActions')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {week.days.map(day => {
                                                const dayEntries =
                                                    entriesByDay.get(day.date) ??
                                                    []
                                                const dayExpanded =
                                                    expandedDays.has(day.date)
                                                return (
                                                    <Fragment key={day.date}>
                                                        <tr
                                                            className={`border-b border-slate-100 last:border-0 dark:border-slate-800 ${
                                                                day.isHoliday
                                                                    ? 'bg-amber-50/60 dark:bg-amber-950/30'
                                                                    : 'hover:bg-slate-50/70 dark:hover:bg-slate-800/60'
                                                            }`}
                                                        >
                                                    <td className='whitespace-nowrap px-4 py-2.5'>
                                                        {dayEntries.length > 0 && (
                                                            <button
                                                                type='button'
                                                                title={
                                                                    dayExpanded
                                                                        ? t(
                                                                              'workingTime.hideDayEntries',
                                                                          )
                                                                        : t(
                                                                              'workingTime.showDayEntries',
                                                                          )
                                                                }
                                                                aria-label={
                                                                    dayExpanded
                                                                        ? t(
                                                                              'workingTime.hideDayEntries',
                                                                          )
                                                                        : t(
                                                                              'workingTime.showDayEntries',
                                                                          )
                                                                }
                                                                onClick={() =>
                                                                    toggleDay(
                                                                        day.date,
                                                                    )
                                                                }
                                                                className='mr-1 inline-flex h-4 w-4 items-center justify-center text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                                                            >
                                                                {dayExpanded
                                                                    ? '▾'
                                                                    : '▸'}
                                                            </button>
                                                        )}
                                                        <span
                                                            className={
                                                                day.isWorkday
                                                                    ? ''
                                                                    : 'text-slate-500 dark:text-slate-400'
                                                            }
                                                        >
                                                            {dayLabel(day.date)}
                                                        </span>
                                                        {day.isHoliday && (
                                                            <Badge
                                                                tone='amber'
                                                                className='ml-2'
                                                            >
                                                                {t(
                                                                    'workingTime.holiday',
                                                                )}
                                                                {day.holidayFactor !==
                                                                    null &&
                                                                    ` ×${day.holidayFactor}`}
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td className='whitespace-nowrap px-4 py-2.5 text-right tabular-nums'>
                                                        {day.targetSeconds > 0 ? (
                                                            formatHours(
                                                                day.targetSeconds,
                                                            )
                                                        ) : (
                                                            <span className='text-slate-500 dark:text-slate-400'>
                                                                —
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className='whitespace-nowrap px-4 py-2.5 text-right tabular-nums'>
                                                        {day.workedSeconds > 0 ? (
                                                            formatHours(
                                                                day.workedSeconds,
                                                            )
                                                        ) : (
                                                            <span className='text-slate-500 dark:text-slate-400'>
                                                                —
                                                            </span>
                                                        )}
                                                    </td>
                                                    {showNight && (
                                                        <td className='whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-indigo-600 dark:text-indigo-400'>
                                                            {day.nightSeconds > 0
                                                                ? formatHours(
                                                                      day.nightSeconds,
                                                                  )
                                                                : '—'}
                                                        </td>
                                                    )}
                                                    <td className='whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-slate-500 dark:text-slate-400'>
                                                        {day.autoBreakSeconds > 0
                                                            ? `−${formatHours(day.autoBreakSeconds)}`
                                                            : '—'}
                                                    </td>
                                                    <td className='whitespace-nowrap px-4 py-2.5 text-right tabular-nums'>
                                                        {absenceCell(day, t)}
                                                    </td>
                                                    <td
                                                        className={`whitespace-nowrap px-4 py-2.5 text-right font-medium tabular-nums ${
                                                            day.saldoSeconds > 0
                                                                ? 'text-emerald-600 dark:text-emerald-400'
                                                                : day.saldoSeconds <
                                                                    0
                                                                  ? 'text-rose-600 dark:text-rose-400'
                                                                  : 'text-slate-500 dark:text-slate-400'
                                                        }`}
                                                    >
                                                        {day.saldoSeconds !== 0
                                                            ? formatHoursSigned(
                                                                  day.saldoSeconds,
                                                              )
                                                            : '0 h'}
                                                    </td>
                                                    <td className='whitespace-nowrap px-4 py-2.5 text-right'>
                                                        <button
                                                            type='button'
                                                            title={
                                                                day.isHoliday
                                                                    ? t('workingTime.holidayEdit')
                                                                    : t('workingTime.holidayMark')
                                                            }
                                                            disabled={
                                                                rowActionsDisabled
                                                            }
                                                            onClick={() =>
                                                                toggleHoliday(day)
                                                            }
                                                            className={`ml-1 px-1 text-base leading-none disabled:cursor-not-allowed disabled:opacity-40 ${
                                                                day.isHoliday
                                                                    ? 'text-amber-500 hover:text-amber-600'
                                                                    : 'text-slate-300 hover:text-amber-500 dark:text-slate-600 dark:hover:text-amber-400'
                                                            }`}
                                                        >
                                                            {day.isHoliday
                                                                ? '★'
                                                                : '☆'}
                                                        </button>
                                                        <button
                                                            type='button'
                                                            title={t(
                                                                'workingTime.addWork',
                                                            )}
                                                            aria-label={t(
                                                                'workingTime.addWork',
                                                            )}
                                                            disabled={
                                                                rowActionsDisabled
                                                            }
                                                            onClick={() =>
                                                                setWorkDate(
                                                                    day.date,
                                                                )
                                                            }
                                                            className='ml-1 px-1 leading-none text-slate-500 hover:text-emerald-600 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-400 dark:hover:text-emerald-400'
                                                        >
                                                            <svg
                                                                className='h-4 w-4'
                                                                fill='none'
                                                                viewBox='0 0 24 24'
                                                                stroke='currentColor'
                                                                strokeWidth={2}
                                                            >
                                                                <circle
                                                                    cx='12'
                                                                    cy='12'
                                                                    r='9'
                                                                />
                                                                <path
                                                                    strokeLinecap='round'
                                                                    strokeLinejoin='round'
                                                                    d='M12 7v5l3 2'
                                                                />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            type='button'
                                                            title={t(
                                                                'workingTime.absenceAdd',
                                                            )}
                                                            disabled={
                                                                rowActionsDisabled
                                                            }
                                                            onClick={() =>
                                                                setAbsenceDate(
                                                                    day.date,
                                                                )
                                                            }
                                                            className='ml-1 px-1 text-base leading-none text-slate-500 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-400 dark:hover:text-indigo-400'
                                                        >
                                                            +
                                                        </button>
                                                    </td>
                                                        </tr>
                                                        {dayExpanded &&
                                                            dayEntries.length >
                                                                0 && (
                                                                <DayEntriesRow
                                                                    workspaceId={
                                                                        workspaceId
                                                                    }
                                                                    entries={
                                                                        dayEntries
                                                                    }
                                                                    colSpan={
                                                                        showNight
                                                                            ? 9
                                                                            : 8
                                                                    }
                                                                    actionsDisabled={
                                                                        rowActionsDisabled
                                                                    }
                                                                    onEdit={
                                                                        setEditingEntry
                                                                    }
                                                                    onDelete={
                                                                        setDeleteEntry
                                                                    }
                                                                />
                                                            )}
                                                    </Fragment>
                                                )
                                            })}
                                            <tr className='border-t border-slate-200 bg-slate-50/70 text-sm font-semibold dark:border-slate-700 dark:bg-slate-800/60'>
                                                <td className='px-4 py-2.5'>
                                                    {t('workingTime.weekSum')}
                                                </td>
                                                <td className='px-4 py-2.5 text-right tabular-nums'>
                                                    {formatHours(
                                                        week.totals.targetSeconds,
                                                    )}
                                                </td>
                                                <td className='px-4 py-2.5 text-right tabular-nums'>
                                                    {formatHours(
                                                        week.totals.workedSeconds,
                                                    )}
                                                </td>
                                                {showNight && (
                                                    <td className='px-4 py-2.5 text-right tabular-nums text-indigo-600 dark:text-indigo-400'>
                                                        {formatHours(
                                                            week.totals.nightSeconds,
                                                        )}
                                                    </td>
                                                )}
                                                <td className='px-4 py-2.5 text-right tabular-nums'>
                                                    {week.totals.autoBreakSeconds >
                                                    0
                                                        ? `−${formatHours(week.totals.autoBreakSeconds)}`
                                                        : '—'}
                                                </td>
                                                <td className='px-4 py-2.5 text-right' />
                                                <td
                                                    className={`px-4 py-2.5 text-right tabular-nums ${
                                                        week.totals.saldoSeconds >
                                                        0
                                                            ? 'text-emerald-600 dark:text-emerald-400'
                                                            : week.totals
                                                                    .saldoSeconds <
                                                                0
                                                              ? 'text-rose-600 dark:text-rose-400'
                                                              : ''
                                                    }`}
                                                >
                                                    {formatHoursSigned(
                                                        week.totals.saldoSeconds,
                                                    )}
                                                </td>
                                                <td className='px-4 py-2.5' />
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </CardBody>
                        </Card>
                    ))}

                    {/* Monatssumme */}
                    <Card>
                        <CardBody className='flex flex-wrap items-center justify-between gap-3'>
                            <span className='text-sm font-semibold'>
                                {t('workingTime.monthTotal')} · {monthName}
                            </span>
                            <div className='flex flex-wrap items-center gap-4 text-sm tabular-nums'>
                                <span className='text-slate-500 dark:text-slate-400'>
                                    {t('workingTime.colTarget')}:{' '}
                                    {formatHours(
                                        data.monthTotals.targetSeconds,
                                    )}
                                </span>
                                <span className='text-slate-500 dark:text-slate-400'>
                                    {t('workingTime.colWorked')}:{' '}
                                    {formatHours(
                                        data.monthTotals.workedSeconds,
                                    )}
                                </span>
                                <span
                                    className={`font-semibold ${
                                        data.monthTotals.saldoSeconds > 0
                                            ? 'text-emerald-600 dark:text-emerald-400'
                                            : data.monthTotals.saldoSeconds < 0
                                              ? 'text-rose-600 dark:text-rose-400'
                                              : ''
                                    }`}
                                >
                                    {t('workingTime.colSaldo')}:{' '}
                                    {formatHoursSigned(
                                        data.monthTotals.saldoSeconds,
                                    )}
                                </span>
                            </div>
                        </CardBody>
                    </Card>
                </>
            )}

            {/* Feiertag bearbeiten (Faktor-Override + Notiz, entfernen) */}
            {holidayDialog !== null && (
                <HolidayEditDialog
                    date={holidayDialog.date}
                    initialFactor={holidayDialog.factor}
                    initialNote={holidayDialog.note}
                    defaultFactor={data?.settings.holidayFactor ?? 2}
                    saving={upsertMark.isPending}
                    onClose={() => setHolidayDialog(null)}
                    onSave={(factor, note) => {
                        upsertMark.mutate(
                            {
                                date: holidayDialog.date,
                                factor: factor === '' ? null : Number(factor),
                                note: note === '' ? null : note,
                                ...(markUserId !== undefined
                                    ? { userId: markUserId }
                                    : {}),
                            },
                            {
                                onSuccess: () => {
                                    toast.success(t('workingTime.holidaySaved'))
                                    setHolidayDialog(null)
                                },
                                onError: err => toast.error(errText(err)),
                            },
                        )
                    }}
                    onRemove={() => {
                        removeMark.mutate(
                            {
                                date: holidayDialog.date,
                                ...(markUserId !== undefined
                                    ? { userId: markUserId }
                                    : {}),
                            },
                            {
                                onSuccess: () => {
                                    toast.success(t('workingTime.holidayRemoved'))
                                    setHolidayDialog(null)
                                },
                                onError: err => toast.error(errText(err)),
                            },
                        )
                    }}
                />
            )}

            {/* Abwesenheit für einen konkreten Tag eintragen */}
            {absenceDate !== null && workspaceId !== null && (
                <AbsenceDialog
                    open
                    onClose={() => setAbsenceDate(null)}
                    workspaceId={workspaceId}
                    initialDate={absenceDate}
                    {...(markUserId !== undefined ? { userId: markUserId } : {})}
                />
            )}

            {/* Arbeit für einen konkreten Tag erfassen */}
            <EntryDialog
                open={workDate !== null}
                onClose={() => setWorkDate(null)}
                workspaceId={workspaceId}
                initialDate={workDate ?? undefined}
                {...(markUserId !== undefined ? { userId: markUserId } : {})}
            />

            {/* Eintrag aus der Tages-Detailzeile bearbeiten */}
            <EntryDialog
                open={editingEntry !== null}
                onClose={() => setEditingEntry(null)}
                workspaceId={workspaceId}
                entry={editingEntry}
            />

            {/* Eintrag aus der Tages-Detailzeile löschen */}
            <ConfirmDialog
                open={deleteEntry !== null}
                message={t('entries.deleteConfirm')}
                confirmLabel={t('common.delete')}
                loading={removeEntry.isPending}
                onClose={() => setDeleteEntry(null)}
                onConfirm={() => {
                    if (deleteEntry === null) return
                    removeEntry.mutate(deleteEntry.id, {
                        onSuccess: () => {
                            toast.success(t('entries.entryDeleted'))
                            setDeleteEntry(null)
                        },
                        onError: err => toast.error(errText(err)),
                    })
                }}
            />

            <ConfirmDialog
                open={confirmClose}
                message={t('workingTime.closeConfirm', { month: monthName })}
                confirmLabel={t('workingTime.closeMonth')}
                loading={closeMonth.isPending}
                onClose={() => setConfirmClose(false)}
                onConfirm={handleCloseMonth}
            />
            <ConfirmDialog
                open={confirmReopen}
                message={t('workingTime.reopenConfirm', { month: monthName })}
                confirmLabel={t('workingTime.reopenMonth')}
                tone='primary'
                loading={reopenMonth.isPending}
                onClose={() => setConfirmReopen(false)}
                onConfirm={handleReopenMonth}
            />
        </div>
    )
}

/** Feiertags-Markierung: Faktor-Override + Notiz, oder entfernen (F5). */
function HolidayEditDialog({
    date,
    initialFactor,
    initialNote,
    defaultFactor,
    saving,
    onClose,
    onSave,
    onRemove,
}: {
    date: string
    initialFactor: string
    initialNote: string
    defaultFactor: number
    saving: boolean
    onClose: () => void
    onSave: (factor: string, note: string) => void
    onRemove: () => void
}) {
    const t = useT()
    const [factor, setFactor] = useState(initialFactor)
    const [note, setNote] = useState(initialNote)

    return (
        <Dialog
            open
            onClose={onClose}
            title={`${t('workingTime.holidayEdit')} · ${date}`}
        >
            <div className='space-y-4'>
                <Field
                    label={t('workingTime.holidayFactorLabel', {
                        default: String(defaultFactor),
                    })}
                >
                    <Input
                        type='number'
                        min='0.01'
                        step='0.25'
                        value={factor}
                        onChange={e => setFactor(e.target.value)}
                        placeholder={String(defaultFactor)}
                    />
                </Field>
                <Field label={t('workingTime.holidayNoteLabel')}>
                    <Input
                        value={note}
                        onChange={e => setNote(e.target.value)}
                        maxLength={200}
                    />
                </Field>
                <div className='flex justify-between gap-2'>
                    <Button variant='danger' onClick={onRemove}>
                        {t('workingTime.holidayRemove')}
                    </Button>
                    <div className='flex gap-2'>
                        <Button variant='secondary' onClick={onClose}>
                            {t('common.cancel')}
                        </Button>
                        <Button onClick={() => onSave(factor, note)} loading={saving}>
                            {t('common.save')}
                        </Button>
                    </div>
                </div>
            </div>
        </Dialog>
    )
}

/**
 * Aufgeklappte Detailzeile unter einem Tag: von/bis-Zeiten der Einträge.
 * Abwesenheiten werden mit Badge aufgeführt, sind hier aber nur löschbar
 * (Bearbeiten weiter über den Abwesenheits-Dialog: löschen + neu anlegen,
 * wegen Volltag/halber Tag/Stunden-Deckelung).
 */
function DayEntriesRow({
    workspaceId,
    entries,
    colSpan,
    actionsDisabled,
    onEdit,
    onDelete,
}: {
    workspaceId: number
    entries: TimeEntry[]
    colSpan: number
    actionsDisabled: boolean
    onEdit: (entry: TimeEntry) => void
    onDelete: (entry: TimeEntry) => void
}) {
    const t = useT()
    const projects = useProjects(workspaceId)
    const clients = useClients(workspaceId)
    const projectName = (id: number | null) =>
        id === null
            ? ''
            : (projects.data ?? []).find(p => p.id === id)?.name ?? `#${id}`
    const clientName = (id: number | null) =>
        id === null
            ? ''
            : (clients.data ?? []).find(c => c.id === id)?.name ?? `#${id}`

    return (
        <tr className='border-b border-slate-100 last:border-0 bg-slate-50/60 dark:border-slate-800 dark:bg-slate-900/40'>
            <td colSpan={colSpan} className='px-4 py-2'>
                <ul className='divide-y divide-slate-100 dark:divide-slate-800'>
                    {entries.map(e => (
                        <li
                            key={e.id}
                            className='flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5 text-sm'
                        >
                            <span className='shrink-0 tabular-nums text-slate-600 dark:text-slate-300'>
                                {formatTime(e.startTime)}
                                {e.endTime
                                    ? `–${formatTime(e.endTime)}`
                                    : ''}
                            </span>
                            {!e.endTime && (
                                <span className='inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-red-600 dark:text-red-400'>
                                    <span className='h-1.5 w-1.5 animate-pulse rounded-full bg-red-500' />
                                    {t('entries.running')}
                                </span>
                            )}
                            <Badge
                                tone={entryTypeMeta[e.entryType].tone}
                                className='shrink-0'
                            >
                                {t(entryTypeMeta[e.entryType].labelKey)}
                            </Badge>
                            <span className='min-w-0 flex-1 truncate text-slate-500 dark:text-slate-400'>
                                {e.description || '—'}
                                {projectName(e.projectId) && (
                                    <span className='ml-2 text-xs text-slate-500 dark:text-slate-400'>
                                        {projectName(e.projectId)}
                                        {clientName(e.clientId) &&
                                            ` · ${clientName(e.clientId)}`}
                                    </span>
                                )}
                            </span>
                            <span className='shrink-0 font-medium tabular-nums text-slate-700 dark:text-slate-200'>
                                {e.endTime
                                    ? formatDurationCompact(e.duration ?? 0)
                                    : '—'}
                            </span>
                            {!ABSENCE_TYPES.has(e.entryType) && (
                                <button
                                    type='button'
                                    title={t('common.edit')}
                                    disabled={actionsDisabled}
                                    onClick={() => onEdit(e)}
                                    className='shrink-0 px-1 text-base leading-none text-slate-500 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-400 dark:hover:text-indigo-400'
                                >
                                    ✎
                                </button>
                            )}
                            <button
                                type='button'
                                title={t('common.delete')}
                                disabled={actionsDisabled}
                                onClick={() => onDelete(e)}
                                className='shrink-0 px-1 text-base leading-none text-slate-500 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-400 dark:hover:text-red-400'
                            >
                                ✕
                            </button>
                        </li>
                    ))}
                </ul>
            </td>
        </tr>
    )
}
